import type { UserRole } from '@/types/user'
import type { Scenario } from '@/types/scenario'

/**
 * Permission helper to check user authorization for various operations.
 * Based on the authorization matrix:
 *
 * - Events (create/edit/delete): Admin OR Exercise Director (scenario) OR Coordinator (scenario)
 * - Scenario modifications: Admin OR Exercise Director (scenario)
 * - User management: Admin only
 * - Scenario deletion: Admin only
 */

export interface PermissionContext {
  userGlobalRole: UserRole
  userId?: string
  scenario?: Scenario | null
}

/**
 * Log permission denial to console for debugging
 */
function logPermissionDenied(
  action: string,
  reason: string,
  context: PermissionContext
) {
  console.warn(
    `[Permission Denied] ${action}:`,
    reason,
    {
      globalRole: context.userGlobalRole,
      userId: context.userId,
      scenarioId: context.scenario?.id,
      scenarioName: context.scenario?.name
    }
  )
}

/**
 * Get the user's role within a specific scenario
 */
export function getUserScenarioRole(
  userId: string | undefined,
  scenario: Scenario | null | undefined
): UserRole | null {
  if (!userId || !scenario) return null

  const participants = scenario.participants || []
  const participant = participants.find(p => p.user_id === userId)
  return participant?.role || null
}

/**
 * Check if user is a global admin
 */
export function isGlobalAdmin(userRole: UserRole): boolean {
  return userRole === 'admin'
}

/**
 * Check if user can create/edit/delete events in a scenario
 */
export function canModifyEvents(context: PermissionContext, logDenial = false): boolean {
  const { userGlobalRole, userId, scenario } = context

  // Global admin can do anything
  if (isGlobalAdmin(userGlobalRole)) {
    return true
  }

  // Check scenario-specific role
  const scenarioRole = getUserScenarioRole(userId, scenario)
  if (!scenarioRole) {
    if (logDenial) {
      logPermissionDenied(
        'Modify Events',
        'User is not a participant in this scenario',
        context
      )
    }
    return false
  }

  const canModify = scenarioRole === 'exercise_director' || scenarioRole === 'coordinator'

  if (!canModify && logDenial) {
    logPermissionDenied(
      'Modify Events',
      `Insufficient role: ${scenarioRole} (requires exercise_director or coordinator)`,
      context
    )
  }

  return canModify
}

/**
 * Check if user can modify scenario settings (not including events)
 */
export function canModifyScenario(context: PermissionContext, logDenial = false): boolean {
  const { userGlobalRole, userId, scenario } = context

  // Global admin can do anything
  if (isGlobalAdmin(userGlobalRole)) {
    return true
  }

  // Check scenario-specific role - only exercise directors
  const scenarioRole = getUserScenarioRole(userId, scenario)
  const canModify = scenarioRole === 'exercise_director'

  if (!canModify && logDenial) {
    logPermissionDenied(
      'Modify Scenario',
      scenarioRole
        ? `Insufficient role: ${scenarioRole} (requires exercise_director)`
        : 'User is not a participant in this scenario',
      context
    )
  }

  return canModify
}

/**
 * Check if user can delete a scenario
 */
export function canDeleteScenario(userRole: UserRole, logDenial = false): boolean {
  const canDelete = isGlobalAdmin(userRole)

  if (!canDelete && logDenial) {
    console.warn(
      '[Permission Denied] Delete Scenario:',
      `Insufficient role: ${userRole} (requires admin)`
    )
  }

  return canDelete
}

/**
 * Check if user can create/manage users
 */
export function canManageUsers(userRole: UserRole, logDenial = false): boolean {
  const canManage = isGlobalAdmin(userRole)

  if (!canManage && logDenial) {
    console.warn(
      '[Permission Denied] Manage Users:',
      `Insufficient role: ${userRole} (requires admin)`
    )
  }

  return canManage
}

/**
 * Check if user can view a scenario (any participant or admin)
 */
export function canViewScenario(context: PermissionContext, logDenial = false): boolean {
  const { userGlobalRole, userId, scenario } = context

  // Global admin can view anything
  if (isGlobalAdmin(userGlobalRole)) {
    return true
  }

  // Check if user is a participant
  const scenarioRole = getUserScenarioRole(userId, scenario)
  const canView = scenarioRole !== null

  if (!canView && logDenial) {
    logPermissionDenied(
      'View Scenario',
      'User is not a participant in this scenario',
      context
    )
  }

  return canView
}

/**
 * Check if user can modify working groups in a scenario
 */
export function canModifyWorkingGroups(context: PermissionContext, logDenial = false): boolean {
  return canModifyScenario(context, logDenial)
}

/**
 * Check if user can modify threads in a scenario
 */
export function canModifyThreads(context: PermissionContext, logDenial = false): boolean {
  return canModifyScenario(context, logDenial)
}

/**
 * Check if user can manage scenario participants
 */
export function canManageParticipants(context: PermissionContext, logDenial = false): boolean {
  return canModifyScenario(context, logDenial)
}

/**
 * Check if user can reorder timeline elements (threads, events via drag/drop)
 * Only admins and exercise directors can reorder timeline elements.
 * Coordinators cannot reorder.
 */
export function canReorderTimeline(context: PermissionContext, logDenial = false): boolean {
  return canModifyScenario(context, logDenial)
}

/**
 * Check if user is a coordinator in the scenario
 * Coordinators have view-only access to timelines but can edit event details
 */
export function isCoordinator(context: PermissionContext): boolean {
  const { userId, scenario } = context
  const scenarioRole = getUserScenarioRole(userId, scenario)
  return scenarioRole === 'coordinator'
}

/**
 * Get a human-readable role display name
 */
export function getRoleDisplayName(role: UserRole): string {
  const roleMap: Record<UserRole, string> = {
    admin: 'Admin',
    exercise_director: 'Exercise Director',
    coordinator: 'Coordinator',
    observer: 'Observer'
  }
  return roleMap[role] || role
}

/**
 * Check if a role allows read-only access
 */
export function isReadOnlyRole(role: UserRole | null): boolean {
  return role === 'observer'
}
